import { ExternalLink, MonitorDown } from "lucide-react";
import { CommandCopyBox } from "@/components/common/command-copy-box";
import { runnerUnavailableReason } from "@/lib/grooming/runner-bridge";
import { TOOLBOX_SETUP_GUIDE_URL, type ToolsetSupport } from "@/lib/toolbox/bridge";

export interface RunnerFallbackProps {
  support: ToolsetSupport | undefined;
  /** The in-session slash command that does the same for one item, when there is one. */
  command?: { label: string; command: string; helperText: string };
}

/**
 * Shown beside a disabled run action when the grooming runner is not
 * available: why, "Open in SpecTree Toolbox" guidance in a browser, and the
 * equivalent in-session slash command when there is one. Copy boxes always
 * carry slash commands, never `claude --agent` (authoring-governance).
 */
export function RunnerFallback({ support, command }: RunnerFallbackProps) {
  const reason = runnerUnavailableReason(support);
  if (reason === null) return null;
  const inBrowser = support === "browser";

  return (
    <div className="space-y-3 rounded-lg border border-border bg-[#111113] p-3" data-testid="runner-fallback">
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <MonitorDown className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span>
          {inBrowser && <span className="font-medium text-foreground">Open in SpecTree Toolbox. </span>}
          {reason}{" "}
          {inBrowser && (
            <a
              href={TOOLBOX_SETUP_GUIDE_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-link hover:underline"
            >
              How to install it
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
        </span>
      </p>
      {command && (
        <CommandCopyBox label={command.label} command={command.command} helperText={command.helperText} />
      )}
    </div>
  );
}
